<template>
  <v-container>
    <v-card :loading="false">

      <v-overlay :absolute="true" :value="isComponentLoading">
        <v-progress-circular indeterminate size="24"></v-progress-circular>
      </v-overlay>

      <div class="filter-group">
        <div class="filter-group__title">{{ $t('clients.filter_group_search') }}</div>
        <v-row class="px-3">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="searchText"
              :disabled="isSubscriptionExpiryFilterActive"
              :hint="lang($store, 'sap/listings.global.filter.hints.search_name')"
              persistent-hint
              single-line
              :loading="false"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="searchParams.sortByOrder"
              :hint="lang($store, 'sap/listings.global.filter.hints.sort_by_orders')"
              :items="sortByOrdersItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
          <v-col v-if="searchParams.sortByOrder" cols="12" md="2">
            <v-select
              v-model="searchParams.direction"
              :hint="lang($store, 'sap/listings.global.filter.hints.sort_direction')"
              :items="sortDirectionItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
        </v-row>
        <v-row class="px-3">
          <v-col cols="12" md="6">
            <v-menu
              v-model="menuFrom"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="fromDateFormatted"
                  :disabled="isSubscriptionExpiryFilterActive || isLastOrderPeriodActive"
                  @input="changeDate('from', $event)"
                  :label="lang($store, 'sap/listings.global.filter.hints.date_from')"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="searchParams.from" no-title
                             :first-day-of-week="1"
                             :locale="getCurrentLocale()"
                             @input="menuFrom = false"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="6">
            <v-menu
              v-model="menuTo"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="toDateFormatted"
                  :disabled="isSubscriptionExpiryFilterActive || isLastOrderPeriodActive"
                  @input="changeDate('to', $event)"
                  :label="lang($store, 'sap/listings.global.filter.hints.date_to')"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="searchParams.to" no-title
                             :first-day-of-week="1"
                             :locale="getCurrentLocale()"
                             @input="menuTo = false"></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </div>

      <div class="filter-group">
        <v-row class="px-3">
          <v-col v-if="sourcesItems" cols="12" md="3">
            <v-select
              v-model="searchParams.source"
              :disabled="isSubscriptionExpiryFilterActive"
              :hint="lang($store, 'sap/listings.global.filter.hints.source')"
              :items="sourcesItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
          <v-col v-if="subscriptionItems" cols="12" md="3">
            <v-select
              v-model="searchParams.subscription"
              :disabled="isSubscriptionExpiryFilterActive"
              :hint="lang($store, 'sap/listings.global.filter.hints.subscription')"
              :items="subscriptionItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
          <v-col v-if="regionsItems" cols="12" md="2">
            <v-select
              v-model="searchParams.region"
              :disabled="isSubscriptionExpiryFilterActive"
              :hint="lang($store, 'sap/listings.global.filter.hints.region')"
              :items="regionsItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="searchParams.is_commerce"
              :disabled="isSubscriptionExpiryFilterActive"
              :hint="lang($store, 'sap/listings.global.filter.hints.commerce')"
              :items="commerceItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="searchParams.premises_type"
              :disabled="isSubscriptionExpiryFilterActive"
              :hint="lang($store, 'sap/listings.global.filter.hints.premises_type')"
              :items="premisesTypeItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
        </v-row>
      </div>

      <v-divider class="mt-8"></v-divider>

      <div class="filter-group">
        <div class="filter-group__title mb-4">{{ $t('clients.filter_group_attributes') }}</div>
        <v-row class="px-3">
          <v-col cols="12" md="4">
            <v-select
              v-model="searchParams.last_order_status"
              :disabled="isSubscriptionExpiryFilterActive"
              :hint="$t('clients.last_order_status_label')"
              :items="lastOrderStatusItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="searchParams.last_order_days"
              :disabled="isSubscriptionExpiryFilterActive"
              :hint="$t('clients.last_order_age_label')"
              :items="lastOrderAgeItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="searchParams.subscription_expiring_days"
              :hint="$t('clients.subscription_expiring_label')"
              :items="subscriptionExpiringItems"
              persistent-hint
              single-line
              :loading="false"
            ></v-select>
          </v-col>
        </v-row>
        <v-row v-if="isCustomLastOrderPeriod" class="px-3">
          <v-col cols="12" md="6">
            <v-menu
              v-model="menuLastOrderFrom"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="lastOrderFromDateFormatted"
                  :disabled="isSubscriptionExpiryFilterActive"
                  @input="changeDate('last_order_from', $event)"
                  :label="$t('clients.last_order_date_from')"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="searchParams.last_order_from" no-title
                             :first-day-of-week="1"
                             :locale="getCurrentLocale()"
                             @input="menuLastOrderFrom = false"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="6">
            <v-menu
              v-model="menuLastOrderTo"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="lastOrderToDateFormatted"
                  :disabled="isSubscriptionExpiryFilterActive"
                  @input="changeDate('last_order_to', $event)"
                  :label="$t('clients.last_order_date_to')"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="searchParams.last_order_to" no-title
                             :first-day-of-week="1"
                             :locale="getCurrentLocale()"
                             @input="menuLastOrderTo = false"></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </div>

      <download-file-button
        v-if="account.role === MANAGER_TOP"
        :use-download-report-users="true"
        :additional-acceptable-accounts="acceptableAccountsForDownload"
        style="position: absolute; top: 5px; right: 35px"
        @click="$emit('download-excel')"
      />
      <send-file-button
        v-if="account.role === MANAGER_TOP"
        :additional-acceptable-accounts="acceptableAccountsForDownload"
        :use-download-report-users="true"
        style="position: absolute; top: 6px; right: 5px"
        @click="$emit('send-email-excel')"
      />
    </v-card>
  </v-container>
</template>

<script>
/* constants */
/* mixins */
import isComponentLoading from '@/mixins/isComponentLoading';
import regionsFilter from '@/mixins/filters/regionsFilter';
import subscriptionFilter from '@/mixins/filters/subscriptionFilter';
import commerceFilter from '@/mixins/filters/commerceFilter';
import premisesFilter from '@/mixins/filters/premisesTypeFilter';
/* components */
import DownloadFileButton from '@/components/minialerts/DownloadFileButton.vue';
import SendFileButton from '@/components/minialerts/SendEmailFileButton.vue';
import { REGIONS } from '~constants/region';
import { MAIN_SOURCES as sources } from '~constants/sources';
import { MANAGER_TOP } from '@/constants/roles';
import { SORT_BY_ORDERS, SORT_DIRECTIONS, DESC } from '../../constants/sorting';

export default {
  name: 'FilterContacts',
  mixins: [
    isComponentLoading,
    regionsFilter,
    subscriptionFilter,
    commerceFilter,
    premisesFilter,
  ],
  components: {
    DownloadFileButton,
    SendFileButton,
  },
  props: {
    defaultSearchParams: {
      type: Object,
      default: () => ({
        search: '',
        active: 1,
        sortByOrder: null,
        direction: DESC,
        last_order_status: null,
        last_order_days: null,
        last_order_from: null,
        last_order_to: null,
        subscription_expiring_days: null,
        source: null,
        region: null,
        subscription: null,
        is_commerce: null,
        premises_type: null
      }),
    },
  },
  data() {
    return {
      MANAGER_TOP,
      searchText: '',
      searchTextTimeout: null,
      menuFrom: false,
      menuTo: false,
      menuLastOrderFrom: false,
      menuLastOrderTo: false,
      searchParams: {}
    };
  },
  mounted() {
    this.searchParams = {
      search: this.defaultSearchParams.search,
      active: this.defaultSearchParams.active,
      sortByOrder: this.defaultSearchParams.sortByOrder,
      direction: this.defaultSearchParams.direction,
      last_order_status: this.defaultSearchParams.last_order_status,
      last_order_days: this.defaultSearchParams.last_order_days,
      last_order_from: this.defaultSearchParams.last_order_from,
      last_order_to: this.defaultSearchParams.last_order_to,
      subscription_expiring_days: this.defaultSearchParams.subscription_expiring_days,
      source: this.defaultSearchParams.source,
      subscription: this.defaultSearchParams.subscription,
      region: this.defaultSearchParams.region,
      is_commerce: this.defaultSearchParams.is_commerce,
      premises_type: this.defaultSearchParams.premises_type
    };

    console.log(`Account is top_manager - ${this.account.role === MANAGER_TOP ? 'true' : 'false'}`);
  },
  computed: {
    sourcesItems() {
      const items = [
        {
          text: this.lang(
            this.$store,
            'sap/listings.global.filter.sources.all'
          ),
          value: null
        }
      ];


      Object.keys(sources).forEach((key) => {
        items.push({
          text: this.lang(
            this.$store,
            `sap/listings.global.filter.sources.${sources[key]}`
          ),
          value: sources[key]
        });
      });
      return items;
    },
    sortByOrdersItems() {
      const items = [
        {
          text: this.lang(
            this.$store,
            'sap/listings.global.filter.contact_sorting_by_orders.no_sort'
          ),
          value: null
        }
      ];
      SORT_BY_ORDERS.forEach(item => {
        items.push({
          text: this.lang(
            this.$store,
            `sap/listings.global.filter.contact_sorting_by_orders.${item}`
          ),
          value: item
        });
      });
      return items;
    },
    sortDirectionItems() {
      return SORT_DIRECTIONS.map(direction => ({
        text: this.lang(
          this.$store,
          `sap/listings.global.filter.sort_direction.${direction}`
        ),
        value: direction
      }));
    },
    lastOrderAgeItems() {
      return [
        { text: this.$t('clients.last_order_age_all'), value: null },
        { text: this.$t('clients.last_order_age_14'), value: 14 },
        { text: this.$t('clients.last_order_age_30'), value: 30 },
        { text: this.$t('clients.last_order_age_180'), value: 180 },
        { text: this.$t('clients.last_order_age_365'), value: 365 },
        { text: this.$t('clients.last_order_age_custom'), value: 'custom' },
      ];
    },
    lastOrderStatusItems() {
      return [
        { text: this.$t('clients.last_order_status_all'), value: null },
        { text: this.$t('clients.last_order_status_declined'), value: 'declined' },
      ];
    },
    subscriptionExpiringItems() {
      return [
        { text: this.$t('clients.subscription_expiring_all'), value: null },
        { text: this.$t('clients.subscription_expiring_10'), value: 10 },
      ];
    },
    isSubscriptionExpiryFilterActive() {
      return Boolean(this.searchParams.subscription_expiring_days);
    },
    isLastOrderPeriodActive() {
      return Boolean(this.searchParams.last_order_days);
    },
    isCustomLastOrderPeriod() {
      return this.searchParams.last_order_days === 'custom';
    },
    account() {
      return this.$store.getters['account/account'];
    },
    acceptableAccountsForDownload() {
      return this.account.phone === '+380 (97) 811-35-44' ? [this.account.id] : [];
    },
    fromDateFormatted() {
      return this.formatDate(this.searchParams.from);
    },
    toDateFormatted() {
      return this.formatDate(this.searchParams.to);
    },
    lastOrderFromDateFormatted() {
      return this.formatDate(this.searchParams.last_order_from);
    },
    lastOrderToDateFormatted() {
      return this.formatDate(this.searchParams.last_order_to);
    }
  },
  watch: {
    'searchParams.subscription_expiring_days': {
      handler(value) {
        if (!value) return;

        this.searchText = '';
        Object.assign(this.searchParams, {
          search: '',
          source: null,
          subscription: null,
          region: null,
          is_commerce: null,
          premises_type: null,
          from: null,
          to: null,
          last_order_status: null,
          last_order_days: null,
          last_order_from: null,
          last_order_to: null,
        });
      },
    },
    'searchParams.last_order_days': {
      handler(value) {
        if (value !== 'custom') {
          this.searchParams.last_order_from = null;
          this.searchParams.last_order_to = null;
        }
      },
    },
    searchParams: {
      handler(newVal) {
        this.$emit('filtering', newVal);
      },
      deep: true
    },
    searchText(newVal) {
      clearTimeout(this.searchTextTimeout);
      this.searchTextTimeout = setTimeout(() => {
        this.searchParams.search = newVal;
      }, 500);
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },
    changeDate(type, date) {
      if (date === null) {
        this.searchParams[type] = null;
      }
    },
    getCurrentLocale() {
      return window.localStorage.language;
    }
  }
};
</script>

<style scoped>
.filter-group__title {
  padding: 12px 12px 0;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  font-weight: 500;
}
</style>
